/**
 * fraudPointBandsApi — read + write the FraudPoint risk-band cut-offs.
 *
 * Risk Management owns these numbers. They live as two SystemSettings rows
 * so they can be moved without a deploy:
 *
 *   lexisnexis.fraudpoint.lowFloor       score at/above which = Low (proceed)
 *   lexisnexis.fraudpoint.moderateFloor  score at/above which = Moderate;
 *                                        below it = High (decline)
 *
 * PROVENANCE. Legacy procedure "Online Applicants.pdf" pp.2-3 (transcribed in
 * docs/operations/person-modal-inventory/2026-06-03-smoke-test-findings.md D2)
 * and RM compliance-gating review decision 2.2: "Low 750+ / Moderate 500-749 /
 * High below 500".
 *
 * WHY A SCREEN EXISTS. Until 2026-09-23 the same score was banded three ways —
 * the enforcing gate at 750/500 (correct), the DDP adapter at placeholder
 * 800/500/400, and the bridge parser off LexisNexis's own colour glyph. The RM
 * review had already flagged it (1.5): the bands "are currently in different
 * places that disagree for scores between 750 and 799", so "the risk level a
 * banker sees could differ from the level the system acts on". One source,
 * owned here, is the fix.
 *
 * Backend contract:
 *   GET  /v3/config/system-settings/rows   full rows, admin grid shape
 *   POST /v3/admin/system-settings         upsert, one key per call
 *
 * Bearer-only (`Authorization: Bearer <msal>`); no function key ships in the
 * browser bundle. HTTP plumbing mirrors riskApprovalsApi.ts.
 */

import { findSetting, loadRiskSettings, saveRiskSetting } from '@/services/riskSettingsApi';

export const LOW_FLOOR_KEY = 'lexisnexis.fraudpoint.lowFloor';
export const MODERATE_FLOOR_KEY = 'lexisnexis.fraudpoint.moderateFloor';

/** Legacy procedure values — what the backend falls back to if these rows go missing. */
export const PROCEDURE_LOW_FLOOR = 750;
export const PROCEDURE_MODERATE_FLOOR = 500;

/** FraudPoint's own scale. Outside this range a cut-off can never bind. */
export const FRAUDPOINT_MIN = 300;
export const FRAUDPOINT_MAX = 999;

export interface FraudPointBandSettings {
  readonly lowFloor: number;
  readonly moderateFloor: number;
  /** True when neither row was present and the procedure defaults are showing. */
  readonly usingDefaults: boolean;
  readonly lowFloorUpdatedAt?: string;
  readonly moderateFloorUpdatedAt?: string;
  readonly lowFloorUpdatedBy?: string;
  readonly moderateFloorUpdatedBy?: string;
}

function parseFloor(raw: string | null, fallback: number): number {
  if (raw == null) return fallback;
  const n = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(n) ? n : fallback;
}

export async function loadFraudPointBands(): Promise<FraudPointBandSettings> {
  // 2026-09-30 — read through /v3/risk/settings (RiskManager-gated). The
  // previous /v3/config/system-settings/rows + /v3/admin/system-settings pair
  // needed the Admin role, so a RiskManager could open this screen and never save.
  const rows = await loadRiskSettings();
  const low = findSetting(rows, LOW_FLOOR_KEY);
  const moderate = findSetting(rows, MODERATE_FLOOR_KEY);
  return {
    lowFloor: parseFloor(low?.settingValue ?? null, PROCEDURE_LOW_FLOOR),
    moderateFloor: parseFloor(moderate?.settingValue ?? null, PROCEDURE_MODERATE_FLOOR),
    usingDefaults: (low?.usingDefault ?? true) && (moderate?.usingDefault ?? true),
    lowFloorUpdatedAt: low?.updatedAt ?? undefined,
    moderateFloorUpdatedAt: moderate?.updatedAt ?? undefined,
    lowFloorUpdatedBy: low?.updatedBy ?? undefined,
    moderateFloorUpdatedBy: moderate?.updatedBy ?? undefined,
  };
}

/**
 * Reject a pair the backend would refuse to grade against. The backend
 * re-checks this and falls back to procedure if it is violated, so a bad save
 * would not mis-decide a cart — but it would silently stop doing what the
 * screen says, which is worse than refusing here.
 */
export function validateBands(lowFloor: number, moderateFloor: number): string | null {
  if (!Number.isInteger(lowFloor) || !Number.isInteger(moderateFloor)) {
    return 'Both cut-offs must be whole numbers.';
  }
  if (moderateFloor <= 0) return 'The Moderate cut-off must be greater than zero.';
  if (lowFloor <= moderateFloor) {
    return 'The Low cut-off must be higher than the Moderate cut-off, otherwise no score can ever be Moderate.';
  }
  if (lowFloor > FRAUDPOINT_MAX) {
    return `FraudPoint never scores above ${FRAUDPOINT_MAX}, so a Low cut-off above that can never be reached.`;
  }
  if (moderateFloor < FRAUDPOINT_MIN) {
    return `FraudPoint never scores below ${FRAUDPOINT_MIN}, so a Moderate cut-off below that can never bind.`;
  }
  return null;
}

/** Band a score exactly as the backend does — used for the live preview. */
export function classify(score: number, lowFloor: number, moderateFloor: number): 'Low' | 'Moderate' | 'High' {
  if (score >= lowFloor) return 'Low';
  if (score >= moderateFloor) return 'Moderate';
  return 'High';
}

async function upsert(settingKey: string, settingValue: string, reason: string): Promise<void> {
  // The description lives in the server-side catalogue now (RiskSettingsService).
  await saveRiskSetting(settingKey, settingValue, reason);
}

/**
 * Save both cut-offs. NOTE: the backend upsert takes one key per call, so this
 * is two writes and is NOT atomic. The backend resolves both together and
 * falls back to procedure whenever the pair is incoherent, so a failure
 * between the two writes cannot leave carts graded against a half-applied
 * pair — but the caller should reload and show what actually landed.
 */
export async function saveFraudPointBands(
  lowFloor: number,
  moderateFloor: number,
  reason: string,
  current?: { readonly lowFloor: number; readonly moderateFloor: number },
): Promise<void> {
  const invalid = validateBands(lowFloor, moderateFloor);
  if (invalid) throw new Error(invalid);

  // The server refuses any single write that would leave Low at or below
  // Moderate, so when the new Low drops to or below the saved Moderate, move
  // Moderate first. Unchanged values are not re-written (no empty audit rows).
  const writeLow = (): Promise<void> => upsert(LOW_FLOOR_KEY, String(lowFloor), reason);
  const writeModerate = (): Promise<void> => upsert(MODERATE_FLOOR_KEY, String(moderateFloor), reason);
  const lowChanged = current == null || current.lowFloor !== lowFloor;
  const moderateChanged = current == null || current.moderateFloor !== moderateFloor;
  const moderateFirst = current != null && lowFloor <= current.moderateFloor;
  if (moderateFirst) {
    if (moderateChanged) await writeModerate();
    if (lowChanged) await writeLow();
  } else {
    if (lowChanged) await writeLow();
    if (moderateChanged) await writeModerate();
  }
}
